import { AlertTriangle, Flag } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { TypeChip } from "@/components/ui/TypeChip";
import { useActiveOverheadCategories } from "@/hooks/use-overhead-categories";
import { type TimeEntry, useUpdateTimeEntry } from "@/hooks/use-time-entries";
import type { WorkOrder } from "@/hooks/use-work-orders";
import { CATEGORIES } from "@/lib/usg-constants";
import { timeEntryFlaggedEditSchema, timeEntryWriteSchema } from "@/schemas/time-entries";
import { CategoryDropdown } from "../CategoryDropdown";
import { HoursStepper } from "../HoursStepper";
import { WorkOrderCombobox } from "../WorkOrderCombobox";

interface EntryEditDialogProps {
  entry: TimeEntry;
  open: boolean;
  onClose: () => void;
}

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function EntryEditDialog({ entry, open, onClose }: EntryEditDialogProps) {
  const isFlagged = entry.status === "flagged";
  const isOverhead = entry.type === "overhead";

  const { data: activeOverheadCategories = [] } = useActiveOverheadCategories();

  const [date, setDate] = useState(entry.entryDate);
  const [wo, setWO] = useState<WorkOrder | null>(null);
  const [category, setCategory] = useState(entry.category ?? "");
  const [subtask, setSubtask] = useState(entry.subtask ?? "");
  const [overheadCode, setOverheadCode] = useState("");
  const [overheadCategoryId, setOverheadCategoryId] = useState<string | null>(null);
  const [overheadSub, setOverheadSub] = useState(entry.overheadSub ?? "");
  const [overheadSubcategoryId, setOverheadSubcategoryId] = useState<string | null>(
    entry.overheadSubcategoryId ?? null,
  );
  const [hours, setHours] = useState(entry.hours);
  const [notes, setNotes] = useState(entry.notes ?? "");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const overheadInitialized = useRef(false);
  useEffect(() => {
    if (overheadInitialized.current || !entry.overheadCode || activeOverheadCategories.length === 0)
      return;
    overheadInitialized.current = true;
    const cat = activeOverheadCategories.find((c) => c.code === entry.overheadCode);
    if (cat) {
      setOverheadCode(`${cat.code} · ${cat.label}`);
      setOverheadCategoryId(cat.id);
      if (entry.overheadSub) {
        const sub = cat.subcategories.find((s) => s.name === entry.overheadSub);
        setOverheadSubcategoryId(sub?.id ?? null);
      }
    } else {
      setOverheadCode(entry.overheadCode);
      setOverheadCategoryId(entry.overheadCategoryId ?? null);
    }
  }, [entry, activeOverheadCategories]);

  const updateEntry = useUpdateTimeEntry();

  const jobType = wo?.jobType ?? entry.category ?? "";

  const categoryOptions = useMemo(() => {
    if (isOverhead || !jobType) return [];
    const cat = CATEGORIES[jobType];
    return cat ? [cat.label] : [];
  }, [isOverhead, jobType]);

  const subtaskOptions = useMemo(() => {
    if (!category || !jobType) return [];
    return CATEGORIES[jobType]?.subs ?? [];
  }, [category, jobType]);

  const overheadOptions = useMemo(
    () => activeOverheadCategories.map((c) => `${c.code} · ${c.label}`),
    [activeOverheadCategories],
  );

  const selectedOverheadCategory = useMemo(() => {
    if (!overheadCode) return null;
    const code = overheadCode.split(" · ")[0];
    return activeOverheadCategories.find((c) => c.code === code) ?? null;
  }, [overheadCode, activeOverheadCategories]);

  const overheadSubOptions = useMemo(
    () => selectedOverheadCategory?.subcategories.map((s) => s.name) ?? [],
    [selectedOverheadCategory],
  );

  function validate(): boolean {
    const code = overheadCode.split(" · ")[0];
    const e: Record<string, string> = {};

    if (isFlagged) {
      if (isOverhead) {
        if (!hours || hours < 0.5) e.hours = "Minimum 0.5 hours (30 min)";
        else if (Math.round(hours * 2) !== hours * 2) e.hours = "Must be in 30-minute increments";
        else if (hours > 24) e.hours = "Hours cannot exceed 24";
        if (notes.length > 500) e.notes = "Notes cannot exceed 500 characters";
      } else {
        const result = timeEntryFlaggedEditSchema.safeParse({
          hours,
          category: category || entry.category || "",
          subtask: subtask || entry.subtask || "",
          notes: notes || null,
        });
        if (!result.success) {
          for (const issue of result.error.issues) {
            const path = String(issue.path[0] ?? "");
            if (!e[path]) e[path] = issue.message;
          }
        }
      }
    } else {
      const effectiveWoNumber = wo?.woNumber ?? entry.woNumber ?? null;
      const effectiveCategory = wo ? wo.jobType : (entry.category ?? category);
      const result = timeEntryWriteSchema.safeParse({
        entryDate: date,
        type: entry.type,
        woNumber: isOverhead ? null : effectiveWoNumber,
        category: isOverhead ? null : effectiveCategory,
        subtask: isOverhead ? null : subtask,
        overheadCode: isOverhead ? code : null,
        overheadSub: isOverhead ? overheadSub : null,
        hours,
        notes: notes || null,
      });
      if (!result.success) {
        for (const issue of result.error.issues) {
          const path = String(issue.path[0] ?? "");
          const key =
            path === "entryDate"
              ? "date"
              : path === "woNumber"
                ? "wo"
                : path === "overheadCode"
                  ? "overhead"
                  : path;
          if (!e[key]) e[key] = issue.message;
        }
      }
      if (isOverhead && overheadSubOptions.length > 0 && !overheadSub) {
        e.overheadSub = "Please select a subcategory";
      }
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSave() {
    if (!validate()) return;
    const code = overheadCode.split(" · ")[0];

    try {
      await updateEntry.mutateAsync({
        id: entry.id,
        data: {
          ...(isFlagged
            ? {
                hours,
                category: category || entry.category,
                subtask: subtask || entry.subtask,
                notes: notes || null,
                status: "submitted",
              }
            : {
                entryDate: date,
                type: entry.type,
                woNumber: isOverhead ? null : (wo?.woNumber ?? entry.woNumber),
                category: isOverhead ? null : (wo?.jobType ?? entry.category ?? category),
                subtask: isOverhead ? null : subtask,
                overheadCode: isOverhead ? code : null,
                overheadSub: isOverhead ? overheadSub : null,
                overheadCategoryId: isOverhead ? overheadCategoryId : null,
                overheadSubcategoryId: isOverhead ? overheadSubcategoryId : null,
                hours,
                notes: notes || null,
              }),
        },
      });
      onClose();
    } catch {
      // toast handled by mutation
    }
  }

  if (!open) return null;

  const isPending = updateEntry.isPending;
  const title = isFlagged ? "Resolve Flagged Entry" : "Edit Time Entry";
  const saveLabel = isPending ? "Saving…" : isFlagged ? "Save & Resubmit" : "Save Changes";

  return (
    <div
      className="modal-backdrop"
      onClick={() => {
        if (!isPending) onClose();
      }}
    >
      <div className="modal" style={{ width: 640 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <h2 className="modal__title">{title}</h2>
        </div>
        <div className="modal__body">
          {isFlagged && entry.flagManager && (
            <div className="callout callout--warn" style={{ marginBottom: 16 }}>
              <Flag size={18} className="callout__icon" />
              <div>
                <strong className="callout__title">Flagged by {entry.flagManager}</strong>
                <div className="callout__body">{entry.flagNote}</div>
              </div>
            </div>
          )}

          <div className="form-grid">
            {!isFlagged && (
              <div className="form-field">
                <label className="form-field__label" htmlFor="edit-entry-date">
                  Date <span className="required">*</span>
                </label>
                <input
                  id="edit-entry-date"
                  type="date"
                  className={`form-input${errors.date ? " is-error" : ""}`}
                  value={date}
                  max={todayISO()}
                  onChange={(e) => setDate(e.target.value)}
                  style={{ maxWidth: 280 }}
                />
                {errors.date && (
                  <div className="form-field__error">
                    <AlertTriangle size={12} /> {errors.date}
                  </div>
                )}
              </div>
            )}

            {!isOverhead && (
              <>
                <div className="form-field">
                  <div className="form-field__label">
                    <span>Work Order {!isFlagged && <span className="required">*</span>}</span>
                    {isFlagged && (
                      <span className="form-field__hint">
                        Locked — contact your manager to change WO
                      </span>
                    )}
                    {!isFlagged && entry.woNumber && !wo && (
                      <span className="form-field__hint">Current: {entry.woNumber}</span>
                    )}
                  </div>
                  {isFlagged ? (
                    <div className="form-input" style={{ background: "var(--surface-alt)" }}>
                      {entry.woNumber ?? "—"}
                    </div>
                  ) : (
                    <>
                      <WorkOrderCombobox
                        value={wo}
                        onChange={(v) => {
                          setWO(v);
                          setCategory("");
                          setSubtask("");
                        }}
                      />
                      {errors.wo && (
                        <div className="form-field__error">
                          <AlertTriangle size={12} /> {errors.wo}
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="form-field">
                  <div className="form-field__label">
                    <span>
                      Activity Category <span className="required">*</span>
                    </span>
                  </div>
                  <CategoryDropdown
                    options={
                      categoryOptions.length > 0
                        ? categoryOptions
                        : entry.category
                          ? [CATEGORIES[entry.category]?.label ?? entry.category]
                          : []
                    }
                    value={category}
                    onChange={(v) => {
                      setCategory(v);
                      setSubtask("");
                    }}
                    placeholder={jobType ? `Select category for ${jobType}…` : "Locked"}
                    locked={!jobType && !entry.category}
                    lockedMessage="Select a Work Order first"
                    hasError={!!errors.category}
                  />
                  {errors.category && (
                    <div className="form-field__error">
                      <AlertTriangle size={12} /> {errors.category}
                    </div>
                  )}
                </div>

                <div className="form-field">
                  <div className="form-field__label">
                    <span>
                      Subtask <span className="required">*</span>
                    </span>
                  </div>
                  <CategoryDropdown
                    options={
                      subtaskOptions.length > 0
                        ? subtaskOptions
                        : entry.category
                          ? (CATEGORIES[entry.category]?.subs ?? [])
                          : []
                    }
                    value={subtask}
                    onChange={setSubtask}
                    placeholder="Select subtask…"
                    locked={!category && !entry.category}
                    lockedMessage="Select a category first"
                    hasError={!!errors.subtask}
                  />
                  {errors.subtask && (
                    <div className="form-field__error">
                      <AlertTriangle size={12} /> {errors.subtask}
                    </div>
                  )}
                </div>
              </>
            )}

            {isOverhead && !isFlagged && (
              <>
                <div className="form-field">
                  <div className="form-field__label">
                    <span>
                      Overhead Category <span className="required">*</span>
                    </span>
                  </div>
                  <CategoryDropdown
                    options={overheadOptions}
                    value={overheadCode}
                    onChange={(v) => {
                      setOverheadCode(v);
                      setOverheadSub("");
                      setOverheadSubcategoryId(null);
                      const code = v.split(" · ")[0];
                      const cat = activeOverheadCategories.find((c) => c.code === code);
                      setOverheadCategoryId(cat?.id ?? null);
                    }}
                    placeholder="Select overhead code…"
                    hasError={!!errors.overhead}
                  />
                  {errors.overhead && (
                    <div className="form-field__error">
                      <AlertTriangle size={12} /> {errors.overhead}
                    </div>
                  )}
                </div>
                {overheadSubOptions.length > 0 && (
                  <div className="form-field">
                    <div className="form-field__label">
                      <span>
                        Subcategory <span className="required">*</span>
                      </span>
                    </div>
                    <CategoryDropdown
                      options={overheadSubOptions}
                      value={overheadSub}
                      onChange={(v) => {
                        setOverheadSub(v);
                        const sub = selectedOverheadCategory?.subcategories.find(
                          (s) => s.name === v,
                        );
                        setOverheadSubcategoryId(sub?.id ?? null);
                      }}
                      placeholder="Select subcategory…"
                      hasError={!!errors.overheadSub}
                    />
                    {errors.overheadSub && (
                      <div className="form-field__error">
                        <AlertTriangle size={12} /> {errors.overheadSub}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            <div className="form-field">
              <div className="form-field__label">
                <span>
                  Hours <span className="required">*</span>
                </span>
                <span className="form-field__hint">30-minute increments</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <HoursStepper value={hours} onChange={setHours} hasError={!!errors.hours} />
                <TypeChip type={entry.type as "billable" | "overhead"} />
              </div>
              {errors.hours && (
                <div className="form-field__error">
                  <AlertTriangle size={12} /> {errors.hours}
                </div>
              )}
            </div>

            <div className="form-field">
              <label className="form-field__label" htmlFor="edit-entry-notes">
                <span>Notes</span>
                <span className="form-field__hint">Optional · {notes.length}/500</span>
              </label>
              <textarea
                id="edit-entry-notes"
                className={`form-input form-textarea${errors.notes ? " is-error" : ""}`}
                placeholder="Add context, issues, or details…"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={500}
              />
              {errors.notes && (
                <div className="form-field__error">
                  <AlertTriangle size={12} /> {errors.notes}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="modal__footer">
          <button
            type="button"
            className="btn btn--secondary"
            onClick={onClose}
            disabled={isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={handleSave}
            disabled={isPending}
          >
            {saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
